/**
 * CSV Upload Modal.
 *
 * Three steps:
 *   1. User picks an account + file
 *   2. We parse, hash, dedupe against Firestore, run rules → preview
 *   3. User confirms → commit
 *
 * Parsing + dedupe happen entirely client-side so the user sees the
 * "X new / Y duplicate / Z transfers paired" numbers before any write
 * lands. No data leaves the browser unless they hit Confirm.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useCashFlow } from '../contexts/CashFlowContext';
import { parseAnzCsv, sha256Hex } from '../utils/anzCsvParser';
import { parseColesCsv, looksLikeColesCsv } from '../utils/colesCsvParser';
import { parseCbaCsv, looksLikeCbaCsv } from '../utils/cbaCsvParser';
import { hashRowsWithOccurrenceGrouping } from '../utils/transactionHash';
import { classify, extractCounterpartyHint } from '../utils/rulesEngine';
import { extractTransferRef } from '../utils/transferPairing';
import { BankTransaction, ImportPreview } from '../types';

type CsvFormat = 'anz' | 'coles_cc' | 'cba';

interface Props {
  show: boolean;
  anzOnly?: boolean;
  onHide: () => void;
}

export function UploadCsvModal({ show, onHide, anzOnly = false }: Props) {
  const { accounts: allAccounts, rules, commitImport, checkExistingIds } = useCashFlow();
  const accounts = useMemo(() => allAccounts.filter(a => !a.archived && (!anzOnly || a.id.startsWith('anz'))), [allAccounts, anzOnly]);
  const [accountId, setAccountId] = useState<string>(accounts[0]?.id || '');
  const [file, setFile] = useState<File | null>(null);
  const [fileText, setFileText] = useState<string>('');
  const [format, setFormat] = useState<CsvFormat>('anz');
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep accountId in sync if the list loaded after the modal mounted.
  React.useEffect(() => {
    if (!accountId && accounts.length) setAccountId(accounts[0].id);
  }, [accounts, accountId, anzOnly]);

  const reset = useCallback(() => {
    setFile(null);
    setFileText('');
    setPreview(null);
    setError(null);
  }, []);

  /** Read + sniff the file as soon as it's picked, so the format badge
   *  and account auto-selection happen before the preview step. */
  const handleFileChange = useCallback(async (f: File | null) => {
    setFile(f);
    setPreview(null);
    setError(null);
    if (!f) { setFileText(''); return; }
    try {
      const text = await f.text();
      setFileText(text);
      const detected: CsvFormat = looksLikeColesCsv(text) ? 'coles_cc' : looksLikeCbaCsv(text) ? 'cba' : 'anz';
      if (anzOnly && detected !== 'anz') throw new Error('Please choose an ANZ transaction CSV.');
      setFormat(detected);
      // Convenience: auto-select the matching account so card/bank exports
      // can't be imported into the wrong account.
      if (detected === 'coles_cc') {
        const card = accounts.find((a) => a.type === 'credit-card' && !a.archived);
        if (card) setAccountId(card.id);
      } else if (detected === 'cba') {
        const cba = accounts.find((a) => a.id.startsWith('cba') && !a.archived);
        if (cba) setAccountId(cba.id);
      } else {
        const current = accounts.find((a) => a.id === accountId);
        if (current?.type === 'credit-card') {
          const firstBank = accounts.find((a) => a.type !== 'credit-card' && !a.archived);
          if (firstBank) setAccountId(firstBank.id);
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Could not read file');
      setFileText('');
    }
  }, [accounts, accountId, anzOnly]);

  const handleClose = useCallback(() => {
    if (committing) return; // don't allow close mid-commit
    reset();
    onHide();
  }, [committing, reset, onHide]);

  /** Parse + hash + dedupe + classify. Pure client-side work. Handles
   *  both supported formats — the unified row shape is
   *  {date, amount, description} plus optional Coles extras. */
  const buildPreview = useCallback(async () => {
    if (!file || !fileText || !accountId) return;
    setParsing(true);
    setError(null);
    setPreview(null);
    try {
      const account = accounts.find((a) => a.id === accountId);

      // Parse per detected format into the unified shape.
      let rows: Array<{
        date: string; amount: number; description: string;
        txType?: string; bankCategory?: string; merchantName?: string; processedOn?: string;
      }>;
      let errors: Array<{ line: number; text: string; reason: string }>;
      if (format === 'coles_cc') {
        const parsed = parseColesCsv(fileText);
        rows = parsed.rows;
        errors = parsed.errors;
      } else if (format === 'cba') {
        const parsed = parseCbaCsv(fileText);
        rows = parsed.rows;
        errors = parsed.errors;
      } else {
        const parsed = parseAnzCsv(fileText);
        rows = parsed.rows;
        errors = parsed.errors;
      }
      if (!rows.length) {
        throw new Error('File is empty — nothing to import.');
      }

      if (errors.length) throw new Error(`${errors.length} row(s) could not be parsed. First issue on line ${errors[0].line}: ${errors[0].reason}. No transactions were imported.`);
      const fileHash = await sha256Hex(fileText);
      const hashes = await hashRowsWithOccurrenceGrouping(accountId, rows);

      // Dedupe pass: ask Firestore which of these ids already exist.
      const existing = await checkExistingIds(hashes);

      const dateRangeStart = rows.reduce((min, r) => (r.date < min ? r.date : min), rows[0].date);
      const dateRangeEnd   = rows.reduce((max, r) => (r.date > max ? r.date : max), rows[0].date);

      const newTransactions: BankTransaction[] = [];
      const duplicateIds: string[] = [];
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const id = hashes[i];
        if (existing.has(id)) {
          duplicateIds.push(id);
          continue;
        }
        const descriptionCanonical = r.description.toLowerCase().replace(/\s+/g, ' ').trim();

        // Classification.
        //
        // ANZ: if the row looks like a transfer (M-BANKING ref) we leave
        // it 'uncategorised' and let the pairing pass promote it to
        // 'internal_transfer' AFTER commit — an unpaired transfer
        // (sibling not yet imported) belongs in the uncategorised queue,
        // not silently caught by a 'TRANSFER FROM ' rule.
        //
        // Coles: 'CREDIT CARD PAYMENT' rows ARE the receiving side of
        // payments from the user's own bank accounts (the bank itself
        // labels them 'Internal transfers'), so they classify straight
        // to internal_transfer — no cross-bank pairing exists to wait
        // for, and counting them as income would double-book every
        // card repayment.
        const isAnzTransferLike = format === 'anz' && !!extractTransferRef(r.description);
        const isCardPayment = format === 'coles_cc' && /CREDIT CARD PAYMENT/i.test(r.txType || '');
        let category = 'uncategorised';
        let subcategory: string | undefined;
        let scope = account?.defaultScope;
        if (isCardPayment) {
          category = 'internal_transfer';
        } else if (!isAnzTransferLike) {
          const hit = classify(rules, accountId, r.description, descriptionCanonical);
          if (hit) {
            category = hit.category;
            subcategory = hit.subcategory;
            if (hit.scope) scope = hit.scope;
          }
        }

        // Build the object with only defined fields. Firestore rejects
        // `undefined` values outright, so optional fields must be
        // omitted (preferred) or set to null — never undefined.
        const tx: any = {
          id,
          accountId,
          date: r.date,
          amount: r.amount,
          direction: r.amount < 0 ? 'out' : 'in',
          descriptionRaw: r.description,
          descriptionCanonical,
          category,
          counterpartyHint: r.merchantName || extractCounterpartyHint(r.description),
          sourceFormat: format,
          importBatchId: '', // filled by commitImport
        };
        if (subcategory) tx.subcategory = subcategory;
        if (scope) tx.scope = scope;
        if (r.txType) tx.txType = r.txType;
        if (r.bankCategory) tx.bankCategory = r.bankCategory;
        if (r.processedOn) tx.processedOn = r.processedOn;
        newTransactions.push(tx);
      }

      setPreview({
        accountId,
        fileName: file.name,
        fileHash,
        totalRows: rows.length,
        newTransactions,
        duplicateIds,
        parseErrors: errors,
        dateRangeStart,
        dateRangeEnd,
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to parse CSV');
    } finally {
      setParsing(false);
    }
  }, [file, fileText, format, accountId, accounts, rules, checkExistingIds]);

  /** Commit step — uses the already-computed preview as the contract. */
  const handleConfirm = useCallback(async () => {
    if (!preview) return;
    setCommitting(true);
    setError(null);
    try {
      const committed = await commitImport(preview);
      toast.success(`Imported ${committed.insertedCount} new transactions.`);
      reset();
      onHide();
    } catch (e: any) {
      const msg = e?.message || 'Commit failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setCommitting(false);
    }
  }, [preview, commitImport, reset, onHide]);

  const transferCount = useMemo(
    () => preview
      ? preview.newTransactions.filter((t) => t.category === 'internal_transfer' || extractTransferRef(t.descriptionRaw)).length
      : 0,
    [preview],
  );

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop={committing ? 'static' : true}>
      <Modal.Header closeButton={!committing}>
        <Modal.Title>{anzOnly ? 'Import ANZ statement' : 'Upload Bank CSV'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mb-3">
          <label className="form-label fw-semibold mb-1">Account</label>
          <select
            className="form-select"
            value={accountId}
            onChange={(e) => { setAccountId(e.target.value); reset(); }}
            disabled={parsing || committing}
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.displayName}</option>
            ))}
          </select>
          <div className="text-muted small mt-1">
            Which account does this CSV come from? This determines how transactions are grouped and how transfers get paired.
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold mb-1">CSV file</label>
          <input
            type="file"
            accept=".csv,text/csv"
            className="form-control"
            onChange={(e) => { void handleFileChange(e.target.files?.[0] || null); }}
            disabled={parsing || committing}
          />
          <div className="text-muted small mt-1">
            ANZ (headerless, DD/MM/YYYY), Coles credit-card (headered), and CommBank/CBA (4-column, signed amounts) exports are all supported — the format is detected automatically.
          </div>
          {file && fileText && (
            <div className="mt-2">
              <span className={`badge ${format === 'coles_cc' ? 'bg-warning text-dark' : format === 'cba' ? 'bg-warning text-dark' : 'bg-primary'}`}>
                {format === 'coles_cc' ? 'Detected: Coles Credit Card export' : format === 'cba' ? 'Detected: CommBank (CBA) export' : 'Detected: ANZ export'}
              </span>
            </div>
          )}
        </div>

        {file && !preview && (
          <div className="mb-3">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={buildPreview}
              disabled={parsing || !accountId}
            >
              {parsing ? 'Parsing…' : 'Preview import'}
            </button>
          </div>
        )}

        {preview && (
          <div className="border rounded p-3 bg-light-info bg-opacity-25">
            <h6 className="mb-2">Preview</h6>
            <div className="row g-3 mb-3">
              <div className="col-sm-3">
                <div className="text-muted small">File rows</div>
                <div className="fs-3 fw-bold">{preview.totalRows}</div>
              </div>
              <div className="col-sm-3">
                <div className="text-muted small">New</div>
                <div className="fs-3 fw-bold text-success">{preview.newTransactions.length}</div>
              </div>
              <div className="col-sm-3">
                <div className="text-muted small">Duplicate (skip)</div>
                <div className="fs-3 fw-bold text-warning">{preview.duplicateIds.length}</div>
              </div>
              <div className="col-sm-3">
                <div className="text-muted small">Transfer candidates</div>
                <div className="fs-3 fw-bold text-info">{transferCount}</div>
              </div>
            </div>
            <div className="text-muted small mb-2">
              Date range: <strong>{preview.dateRangeStart}</strong> → <strong>{preview.dateRangeEnd}</strong>
            </div>
            {preview.parseErrors.length > 0 && (
              <div className="alert alert-warning small mb-2">
                <strong>{preview.parseErrors.length}</strong> line(s) couldn't be parsed. Showing first 3:
                <ul className="mb-0 ps-3">
                  {preview.parseErrors.slice(0, 3).map((e) => (
                    <li key={e.line}>Line {e.line}: {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            {preview.newTransactions.length === 0 && preview.duplicateIds.length > 0 && (
              <div className="alert alert-info small mb-0">
                Every row in this file is already in the system. Nothing new to import.
              </div>
            )}
            {preview.newTransactions.length > 0 && (
              <div className="text-muted small">
                Transfer-pairing runs automatically after commit — any new rows that match an existing TO/FROM sibling will be tagged as internal transfers.
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={handleClose} disabled={committing}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={!preview || preview.newTransactions.length === 0 || committing}
        >
          {committing ? 'Importing…' : `Import ${preview?.newTransactions.length || 0} transactions`}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default UploadCsvModal;
